
import { useState, useEffect } from 'react';
import { smartAutoSyncService } from '@/services/SmartAutoSyncService';
import { networkService } from '@/services/network.service';
import OfflineLogger from '@/utils/offlineLogger';

interface UseSmartAutoSyncOptions {
  entities?: string[];
  onSyncComplete?: () => void;
  onSyncError?: (error: string) => void;
  enabled?: boolean;
}

export function useSmartAutoSync(options: UseSmartAutoSyncOptions = {}) {
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
  const [syncStatus, setSyncStatus] = useState<string>('idle');

  useEffect(() => {
    if (!options.enabled) return;

    const runOnMount = async () => {
      if (!networkService.isOnline()) return;
      try {
        await smartAutoSyncService.forceSync('page-entry', options.entities);
      } catch (err) {
        OfflineLogger.error('useSmartAutoSync.initSync', err);
      }
    };

    runOnMount();

    const unsubscribe = smartAutoSyncService.addListener((status) => {
      setSyncStatus(status);
      
      if (status.includes('sync-started')) {
        setIsSyncing(true);
      } else if (status.includes('sync-completed')) {
        setIsSyncing(false);
        setLastSyncTime(new Date());
        options.onSyncComplete?.();
      } else if (status.includes('sync-error')) {
        setIsSyncing(false);
        options.onSyncError?.(status);
      }
    });

    return unsubscribe;
  }, [options.enabled, options.onSyncComplete, options.onSyncError]);

  const manualSync = async () => {
    if (!networkService.isOnline()) {
      OfflineLogger.warn('useSmartAutoSync', 'Cannot sync while offline');
      return;
    }

    try {
      setIsSyncing(true);
      if (options.entities && options.entities.length > 0) {
        await smartAutoSyncService.performSmartSync(options.entities);
      } else {
        await smartAutoSyncService.forceSync('manual-trigger');
      }
    } catch (error) {
      OfflineLogger.error('useSmartAutoSync.manualSync', error);
      options.onSyncError?.(error instanceof Error ? error.message : 'Sync failed');
    } finally {
      setIsSyncing(false);
    }
  };

  return {
    isSyncing,
    lastSyncTime,
    syncStatus,
    manualSync
  };
}
